import { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Target, X } from "lucide-react";
import { useCookieConsent } from "../hooks/useCookieConsent";

export default function CookieConsent() {
  const { consent, showBanner, acceptAll, rejectAll, save, setShowBanner } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [localPrefs, setLocalPrefs] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
  });

  if (!showBanner) return null;

  const handleSavePrefs = () => {
    save(localPrefs);
    setShowSettings(false);
  };

  return (
    <>
      {/* Backdrop muy sutil cuando está abierto */}
      <div
        className="fixed inset-0 bg-black/40 z-[60] pointer-events-none"
        aria-hidden="true"
      />

      {/* Banner principal */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[70] p-3 sm:p-4 md:p-6"
        role="dialog"
        aria-labelledby="cookie-title"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-5xl mx-auto bg-[#1a1625] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          {!showSettings ? (
            // ---------- VISTA PRINCIPAL ----------
            <div className="p-5 sm:p-6 md:p-7">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 id="cookie-title" className="text-white font-semibold text-base sm:text-lg mb-1">
                    Tu privacidad importa
                  </h2>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Usamos cookies para mejorar tu experiencia, analizar el tráfico y personalizar
                    contenido. Puedes aceptar todas, rechazar las no esenciales o configurar tus
                    preferencias. Más información en nuestra{" "}
                    <Link
                      to="/cookies"
                      className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                      onClick={() => setShowBanner(false)}
                    >
                      política de cookies
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2.5 sm:justify-end">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white transition-all duration-200 border border-white/10 hover:border-white/20"
                  data-testid="cookie-settings-btn"
                >
                  Configurar
                </button>
                <button
                  onClick={rejectAll}
                  className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white transition-all duration-200 border border-white/10 hover:border-white/20"
                  data-testid="cookie-reject-btn"
                >
                  Rechazar
                </button>
                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                  data-testid="cookie-accept-all-btn"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          ) : (
            // ---------- VISTA CONFIGURACIÓN GRANULAR ----------
            <div className="p-5 sm:p-6 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-semibold text-lg">Configuración de cookies</h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors"
                  aria-label="Volver"
                  data-testid="cookie-settings-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <PreferenceRow
                  icon={ShieldCheck}
                  iconColor="from-emerald-500 to-teal-600"
                  title="Cookies necesarias"
                  description="Imprescindibles para que la web funcione: sesión, seguridad, idioma."
                  checked={true}
                  locked={true}
                  onChange={() => {}}
                />

                <PreferenceRow
                  icon={BarChart3}
                  iconColor="from-violet-500 to-purple-600"
                  title="Analítica"
                  description="Nos ayudan a entender qué funcionalidades usan los visitantes para mejorar la web."
                  checked={localPrefs.analytics}
                  onChange={(v) => setLocalPrefs((p) => ({ ...p, analytics: v }))}
                  testId="cookie-toggle-analytics"
                />

                <PreferenceRow
                  icon={Target}
                  iconColor="from-amber-500 to-orange-600"
                  title="Marketing"
                  description="Permiten mostrar publicidad relevante (Meta Pixel). Sin esto verás anuncios genéricos."
                  checked={localPrefs.marketing}
                  onChange={(v) => setLocalPrefs((p) => ({ ...p, marketing: v }))}
                  testId="cookie-toggle-marketing"
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-2.5 sm:justify-end">
                <button
                  onClick={rejectAll}
                  className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white transition-all duration-200 border border-white/10 hover:border-white/20"
                  data-testid="cookie-settings-reject-btn"
                >
                  Rechazar todas
                </button>
                <button
                  onClick={handleSavePrefs}
                  className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                  data-testid="cookie-save-prefs-btn"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function PreferenceRow({ icon: Icon, iconColor, title, description, checked, onChange, locked, testId }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-[#15121e] rounded-xl border border-white/5">
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${iconColor} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-white text-sm font-medium">{title}</h3>
          <Toggle checked={checked} onChange={onChange} locked={locked} testId={testId} />
        </div>
        <p className="text-gray-500 text-xs mt-1 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, locked, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={locked}
      onClick={() => !locked && onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-gradient-to-r from-amber-500 to-orange-600" : "bg-white/10"
      } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}
